import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { sheetRasterUrl } from "@/lib/api";

/**
 * DensityScrubStrip — vertical "binder thumb-index" for 95+ sheet plan sets.
 *
 * Props:
 *  - sheets        — list of {id, sheet_number, sheet_title, revision, ...}
 *  - activeId      — optional. The sheet the user is currently viewing. The
 *                    matching cell shows a persistent soft glow ("you're here
 *                    in the binder").
 *  - pulseTick     — optional. Any monotonically-changing value (e.g. the
 *                    current sheet id, or Date.now()). When it changes, the
 *                    cell matching `activeId` flashes a brief orange pulse
 *                    — the scrub-strip ↔ canvas handshake.
 *
 * Operational orientation, NOT analytics. Looks/feels like running a thumb
 * down the side of a giant FAA plan set to find the page you want.
 *
 * Each sheet gets ONE cell. The cell shows:
 *   - a subtle warm tint when the sheet was revised in the last 30 days
 *   - a tiny orange dot when the sheet has any open pins (per user directive:
 *     "tiny open-pin presence dot is OK because it preserves field
 *     orientation usefulness without becoming dashboard software")
 *   - a soft current-row glow when the cursor is hovering / touch-scrubbing
 *   - persistent soft glow when activeId === this cell's sheet id
 *   - 600ms orange handshake-pulse when pulseTick changes
 *
 * NOT allowed (per spec): heatmaps, charts, issue scoring, dashboard visuals,
 * management indicators, enterprise chrome.
 */
export default function DensityScrubStrip({
  sheets = [],
  activeId = null,
  pulseTick = null,
  pulseSeverity = null,  // null/'low' | 'medium' | 'high' | 'critical'
  rightOffsetPx = 6,
  "data-testid": testid = "density-scrub-strip",
}) {
  const navigate = useNavigate();
  const stripRef = useRef(null);
  const focusedRef = useRef(null);
  const touchActive = useRef(false);
  const [hoverIdx, setHoverIdx] = useState(-1);  // -1 = not scrubbing
  const [previewBox, setPreviewBox] = useState(null);
  // Local pulse — used for click feedback BEFORE the navigation fires (the
  // user gets the orientation feedback even before the parent passes a new
  // pulseTick down on the new page).
  const [localPulseId, setLocalPulseId] = useState(null);
  const [parentPulseId, setParentPulseId] = useState(null);
  // Tracks the severity that should colour the active pulse. Persisted on
  // the same lifetime as parentPulseId — clears with it.
  const [parentPulseSev, setParentPulseSev] = useState(null);

  const visible = useMemo(() => sheets.filter((s) => s && s.id), [sheets]);

  useEffect(() => { if (hoverIdx >= 0) focusedRef.current?.scrollIntoView({ block: "nearest" }); }, [hoverIdx]);

  // Parent-driven pulse — when pulseTick changes, pulse the activeId cell.
  // We pair the tick with the id so React picks up genuine changes; using a
  // composite key avoids "same id twice in a row" being a no-op.
  useEffect(() => {
    if (!activeId || pulseTick == null) return;
    setParentPulseId(`${activeId}|${pulseTick}`);
    setParentPulseSev(pulseSeverity || null);
    const t = setTimeout(() => {
      setParentPulseId(null);
      setParentPulseSev(null);
    }, 650);
    return () => clearTimeout(t);
  }, [activeId, pulseTick, pulseSeverity]);

  const RECENT_DAYS = 30;
  const isRecent = (s) => {
    if (!s.uploaded_at) return false;
    const dt = new Date(s.uploaded_at).getTime();
    return Date.now() - dt < RECENT_DAYS * 86400000;
  };

  if (visible.length < 8) return null;

  const idxFromEvent = (e) => {
    const rect = stripRef.current?.getBoundingClientRect();
    if (!rect) return -1;
    const y = (e.clientY ?? e.touches?.[0]?.clientY ?? 0) - rect.top;
    const ratio = Math.max(0, Math.min(0.9999, y / rect.height));
    return Math.floor(ratio * visible.length);
  };

  const onMove = (e) => {
    const i = idxFromEvent(e);
    if (i !== hoverIdx) {
      setHoverIdx(i);
      const rect = stripRef.current?.getBoundingClientRect();
      if (rect) {
        const cellY = rect.top + (i / visible.length) * rect.height;
        const top = Math.max(16, Math.min(window.innerHeight - 340, cellY - 160));
        setPreviewBox({ top });
      }
    }
  };
  const onLeave = () => { setHoverIdx(-1); setPreviewBox(null); };

  // Click = local-pulse the source cell, then navigate after a beat so the
  // orientation feedback registers visually before the route transition.
  const triggerNav = (s) => {
    if (!s) return;
    setLocalPulseId(`${s.id}|${Date.now()}`);
    // Fire the pulse for one frame, then move to the sheet.
    setTimeout(() => navigate(`/sheet/${s.id}`), 80);
  };
  const onClick = (e) => triggerNav(visible[idxFromEvent(e)]);

  const onTouchStart = (e) => { touchActive.current = true; onMove(e); };
  const onTouchMove  = (e) => { if (touchActive.current) onMove(e); };
  const onTouchEnd   = () => {
    if (!touchActive.current) return;
    touchActive.current = false;
    const i = hoverIdx;
    if (i >= 0 && i < visible.length) triggerNav(visible[i]);
    onLeave();
  };

  return (
    <>
      <div
        ref={stripRef}
        className="fixed top-16 bottom-4 z-40 w-3.5 rounded-full bg-[var(--fop-surface-2)]/80 border border-[var(--fop-border)] backdrop-blur-sm select-none"
        style={{ right: `${rightOffsetPx}px` }}
        onMouseMove={onMove}
        onMouseLeave={onLeave}
        onClick={onClick}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        data-testid={testid}
        aria-label="Sheet density index"
        role="slider"
        aria-valuemin={0}
        aria-valuemax={visible.length - 1}
        aria-valuenow={hoverIdx >= 0 ? hoverIdx : 0}
      >
        <div className="absolute inset-0.5 flex flex-col gap-px overflow-hidden rounded-full">
          {visible.map((s, i) => {
            const recent = isRecent(s);
            const hasOpen = (s.open_pins || 0) > 0;
            const focused = i === hoverIdx;
            const isActive = activeId && s.id === activeId;
            const localPulse  = localPulseId && localPulseId.startsWith(`${s.id}|`);
            const parentPulse = parentPulseId && parentPulseId.startsWith(`${s.id}|`);
            // Persistent base bg for the active cell — a soft warm hint
            // that says "you're here in the binder". Recent-revision tint
            // still applies underneath, additively via the order of
            // resolution below.
            const baseBg = isActive
              ? "rgba(232, 93, 31, 0.18)"
              : focused
                ? "rgba(255, 255, 255, 0.18)"
                : recent
                  ? "rgba(232, 93, 31, 0.14)"
                  : "transparent";
            const pulseKey = localPulse ? localPulseId : parentPulse ? parentPulseId : null;
            // Severity → CSS variant. Local clicks (navigation handshake)
            // always use the default orange; parent pulses can be coloured.
            let pulseClass = "";
            if (pulseKey) {
              if (localPulse) {
                pulseClass = "scrub-cell-pulse";
              } else if (parentPulseSev === "critical") {
                pulseClass = "scrub-cell-pulse-critical";
              } else if (parentPulseSev === "high") {
                pulseClass = "scrub-cell-pulse-high";
              } else if (parentPulseSev === "medium") {
                pulseClass = "scrub-cell-pulse-medium";
              } else {
                pulseClass = "scrub-cell-pulse";
              }
            }
            return (
              <div
                key={s.id}
                ref={focused ? focusedRef : null}
                className={`relative flex-1 min-h-[2px] transition-colors ${pulseClass}`}
                style={{ background: baseBg }}
                data-testid={`scrub-cell-${i}`}
                data-recent={recent ? "1" : "0"}
                data-has-open-pins={hasOpen ? "1" : "0"}
                data-active={isActive ? "1" : "0"}
                data-pulsing={pulseKey ? "1" : "0"}
                data-pulse-severity={pulseKey && parentPulse ? (parentPulseSev || "low") : ""}
              >
                {hasOpen && (
                  <div
                    className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full"
                    style={{
                      width: 3, height: 3,
                      background: "var(--fop-primary)",
                      boxShadow: "0 0 4px rgba(232, 93, 31, 0.6)",
                    }}
                    aria-hidden
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>

      {hoverIdx >= 0 && hoverIdx < visible.length && previewBox && (
        <ScrubPreview
          sheet={visible[hoverIdx]}
          top={previewBox.top}
          total={visible.length}
          index={hoverIdx}
        />
      )}
    </>
  );
}

/**
 * ScrubPreview — large floating sheet preview while the user scrubs.
 *
 * Positioned to the LEFT of the strip, near the cursor row. Includes the
 * sheet number + title + revision badge so the super can confirm before
 * releasing. Lives long enough to feel responsive (no debounce).
 */
function ScrubPreview({ sheet, top, total, index }) {
  return (
    <div
      className="fixed z-50 right-7 w-[340px] aspect-[4/3] bg-[#0a0a0b] rounded-md border border-[var(--fop-border-hi)] shadow-2xl overflow-hidden pointer-events-none"
      style={{ top }}
      data-testid="scrub-preview"
    >
      <img
        src={sheetRasterUrl(sheet.id, { dpr: 1 })}
        alt=""
        className="absolute inset-0 w-full h-full object-contain"
        decoding="async"
      />
      <div className="absolute top-1.5 left-1.5 right-1.5 flex items-center gap-2 pointer-events-none">
        <div className="px-1.5 py-0.5 rounded bg-black/70 backdrop-blur-sm">
          <div className="font-mono text-[11px] font-bold text-[var(--fop-text)] leading-none">
            {sheet.sheet_number}
          </div>
        </div>
        {sheet.revision ? (
          <div className="px-1.5 py-0.5 rounded bg-black/60 backdrop-blur-sm font-mono text-[9px] uppercase tracking-wider text-[var(--fop-text-3)]">
            REV {sheet.revision}
          </div>
        ) : null}
        <div className="ml-auto px-1.5 py-0.5 rounded bg-black/60 backdrop-blur-sm font-mono text-[9px] text-[var(--fop-text-3)]">
          {index + 1}/{total}
        </div>
      </div>
      {sheet.sheet_title ? (
        <div className="absolute bottom-1.5 left-1.5 right-1.5 px-1.5 py-0.5 rounded bg-black/70 backdrop-blur-sm pointer-events-none">
          <div className="font-mono text-[10px] text-[var(--fop-text-2)] truncate">
            {sheet.sheet_title}
          </div>
        </div>
      ) : null}
    </div>
  );
}
